import { t as tr } from "@/i18n";
import { Link } from "react-router-dom";
import { ShieldCheck, PaintBucket, FolderKanban, Printer, ArrowRight, ScanEye, Sparkles, Lock, Building2, BadgeCheck } from "lucide-react";
import { BUSINESS } from "@/components/Footer";

const HERO = "https://images.unsplash.com/photo-1637195141546-2469a5312504?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CHILD = "https://images.unsplash.com/photo-1620398722262-969d8f2bc875?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const CLASS = "https://images.unsplash.com/photo-1758685733940-b1c11d04f553?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const Feature = ({ icon: Icon, title, text, color, delay }) => (
  <div className={`card-soft p-7 hover:border-slate-300 rise ${delay}`}>
    <div className={`w-14 h-14 rounded-2xl ${color} border-2 border-[#1E293B] flex items-center justify-center mb-5 shadow-[2px_3px_0_0_#1E293B]`}>
      <Icon className="w-7 h-7 text-[#1E293B]" />
    </div>
    <h3 className="font-display text-xl font-semibold mb-2">{title}</h3>
    <p className="text-slate-600 leading-relaxed">{text}</p>
  </div>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 space-y-8">
          <span className="safety-badge rise"><ShieldCheck className="w-3.5 h-3.5" /> {tr("two_layer_kid_safety_on_every_page")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] rise d1">
            {tr("type_an_idea")}<br />
            {tr("print_a")} <span className="relative inline-block text-[#FF5252]">{tr("colouring_page")}
              <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" preserveAspectRatio="none"><path d="M2 9 Q 50 1 100 7 T 198 5" stroke="#FFD166" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray="400" style={{ animation: "draw 1.2s ease-out both 400ms" }} /></svg>
            </span><br />{tr("in_seconds")}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed rise d2">
            {tr("paintipop_turns_prompts_into_clean_print")}
          </p>
          <div className="flex flex-wrap gap-4 rise d3">
            <Link to="/signup" className="btn-pop btn-red text-lg" data-testid="hero-get-started-button">
              {tr("start_with_5_free_pages")} <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/login" className="btn-pop btn-white text-lg" data-testid="hero-login-button">{tr("i_have_an_account")}</Link>
          </div>
        </div>
        <div className="lg:col-span-6 relative rise d2">
          <div className="absolute -top-6 -left-4 w-24 h-24 rounded-full bg-[#4ECDC4] border-2 border-[#1E293B]" />
          <div className="absolute -bottom-6 right-10 w-16 h-16 rounded-2xl bg-[#FFD166] border-2 border-[#1E293B] rotate-12" />
          <div className="relative rounded-[2rem] overflow-hidden border-4 border-[#1E293B] shadow-[10px_12px_0_0_#1E293B] rotate-[1.5deg]">
            <img src={HERO} alt={tr("child_colouring")} className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute bottom-8 -left-6 bg-white rounded-2xl p-4 border-2 border-[#1E293B] shadow-[4px_5px_0_0_#1E293B] flex items-center gap-3 rotate-[-3deg]">
            <ScanEye className="w-6 h-6 text-emerald-500" />
            <div>
              <p className="font-bold text-sm">{tr("picture_approved")}</p>
              <p className="text-xs text-slate-500">{tr("scanned_before_it_s_shown")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y-2 border-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">{tr("built_for_little_hands_and_busy_grown_up")}</h2>
          <p className="text-slate-600 text-base sm:text-lg mb-12 max-w-2xl">{tr("two_simple_modes")} <b>{tr("create")}</b> {tr("for_parents_teachers")} <b>{tr("colour")}</b> {tr("for_kids")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Feature icon={ShieldCheck} title={tr("safe_by_design")} text={tr("prompts_are_filtered_before_generation_a")} color="bg-emerald-200" delay="d1" />
            <Feature icon={PaintBucket} title={tr("tap_to_fill_colouring")} text={tr("big_swatches_undo_and_reset_kids_tap_a_s")} color="bg-[#4ECDC4]" delay="d2" />
            <Feature icon={FolderKanban} title={tr("teacher_packets")} text={tr("generate_a_themed_set_reorder_pages_and_")} color="bg-[#FFD166]" delay="d3" />
            <Feature icon={Printer} title={tr("print_perfect_pdfs")} text={tr("high_resolution_a4_pages_with_fun_facts_")} color="bg-[#FF9FF3]" delay="d4" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid md:grid-cols-2 gap-8">
        {[{ img: CHILD, title: tr("for_parents"), text: tr("five_quiet_minutes_on_demand_pick_a_turt"), tag: "Kids colour, you relax" },
          { img: CLASS, title: tr("for_teachers"), text: tr("landmarks_of_the_world_six_pages_fun_fac"), tag: "Packets in one click" }].map((c) => (
          <div key={c.title} className="card-soft overflow-hidden hover:border-slate-300">
            <img src={c.img} alt={c.title} className="w-full h-64 object-cover" />
            <div className="p-7">
              <span className="text-xs font-bold uppercase tracking-wider text-[#FF5252]">{c.tag}</span>
              <h3 className="font-display text-2xl font-semibold mt-1 mb-2">{c.title}</h3>
              <p className="text-slate-600">{c.text}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="card-soft p-6 sm:p-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="trust-strip">
          {[
            { icon: ShieldCheck, title: tr("two_layer_ai_safety"), text: "Ideas are filtered before drawing; every picture is scanned before a child sees it.", to: "/safety" },
            { icon: Lock, title: tr("secure_stripe_checkout"), text: tr("card_details_go_straight_to_stripe_we_ne") },
            { icon: Building2, title: tr("registered_australian_business"), text: `${BUSINESS.name} · ABN ${BUSINESS.abn} · ${BUSINESS.location}` },
            { icon: BadgeCheck, title: tr("fair_refunds"), text: tr("failed_pages_never_cost_a_credit_and_unu") },
          ].map((t) => (
            <div key={t.title} className="flex gap-3">
              <span className="w-10 h-10 shrink-0 rounded-xl bg-emerald-100 border-2 border-[#1E293B] flex items-center justify-center"><t.icon className="w-5 h-5 text-emerald-700" /></span>
              <div><p className="font-bold">{t.title}</p><p className="text-sm text-slate-600">{t.text}</p>
                {t.to && <Link to={t.to} className="text-sm font-bold underline hover:text-[#FF5252]" data-testid="trust-safety-link">{tr("learn_how")}</Link>}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="bg-[#1E293B] rounded-[2rem] p-10 sm:p-14 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">{tr("your_first_5_pages_are_on_us")}</h2>
            <p className="text-slate-300">{tr("then_simple_credit_packs_no_subscription")}</p>
          </div>
          <Link to="/signup" className="btn-pop btn-yellow text-lg shrink-0" data-testid="cta-signup-button"><Sparkles className="w-5 h-5" /> {tr("create_my_account")}</Link>
        </div>
      </section>
    </div>
  );
}
